import { useEffect, useRef, useState } from 'react';

import {
  AppIcon,
  BackrsForm,
  buildSeededMeshConfig,
  InfoModal,
  BackrsFormValues,
  MeshGradient,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { playlistDialogFormConfig } from '@backrs_ui/grow-ui/src/pages/Priorities/configs/playlistDialogForm.config';
import {
  isValidImageType,
  uploadImage,
} from '@backrs_ui/grow-ui/src/pages/Profile/atoms/uploadImage';
import { PriorityPlaylistPost } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Theme, SxProps, Typography, Button, Tooltip } from '@mui/material';
import { useForm } from 'react-hook-form';

const PLAYLIST_DIALOG_STRINGS = appStrings.modules.priorities.playlistDialog;

type PlaylistDialogProps = Readonly<{
  isPendingAddNewNugget: boolean;
  preLoadedTitle?: string;
  playlistToEdit?: PriorityPlaylist | null;
  handleClose: (shouldClosePlaylistDrawer: boolean) => void;
  handlePlaylistCreate: (playlist: PriorityPlaylistPost) => Promise<void>;
  handlePlaylistUpdate?: (playlist: PriorityPlaylistPost) => Promise<void>;
}>;

export const PlaylistDialog = ({
  isPendingAddNewNugget,
  preLoadedTitle = '',
  playlistToEdit,
  handleClose,
  handlePlaylistCreate,
  handlePlaylistUpdate,
}: PlaylistDialogProps) => {
  const fields = playlistDialogFormConfig({
    playlistToEdit,
  });

  const methods = useForm<BackrsFormValues>({
    defaultValues: {
      title: playlistToEdit?.title || preLoadedTitle,
      description: playlistToEdit?.description || '',
      tags: '',
    },
  });

  // Handle tags transformation for editing
  useEffect(() => {
    if (playlistToEdit?.tags && playlistToEdit.tags.length > 0) {
      const tagTitles = playlistToEdit.tags.map((tag) => tag.title);

      (methods.setValue as (name: string, value: unknown) => void)('tags', tagTitles);
    }
  }, [playlistToEdit, methods]);

  const isFormStateValid = methods.formState.isValid;
  const isEditing = !!playlistToEdit;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  const displayedImageUrl =
    imagePreviewUrl ?? (isImageRemoved ? null : (playlistToEdit?.preview_media?.media_url ?? null));

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = '';
    if (!file) return;

    if (!isValidImageType(file)) {
      setImageError(PLAYLIST_DIALOG_STRINGS.invalidImageType);

      return;
    }

    setImageError(null);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setIsImageRemoved(false);
  };

  const handleImageRemove = () => {
    setImageError(null);
    setImageFile(null);
    setImagePreviewUrl(null);
    setIsImageRemoved(true);
  };

  const resolvePreviewMediaId = async (): Promise<
    Pick<PriorityPlaylistPost, 'preview_media_id'>
  > => {
    if (imageFile) {
      setIsUploadingImage(true);
      try {
        const { mediaId } = await uploadImage({ file: imageFile });

        return { preview_media_id: mediaId };
      } finally {
        setIsUploadingImage(false);
      }
    }

    if (isImageRemoved && playlistToEdit?.preview_media_id) {
      return { preview_media_id: null };
    }

    return {};
  };

  const handleSubmit = async (data: BackrsFormValues) => {
    setSubmitError(null);
    try {
      const tags = Array.isArray(data.tags) ? data.tags : [];
      const uniqueTags = tags.filter((tag, index, self) => self.indexOf(tag) === index);

      const constructedPlaylist: PriorityPlaylistPost = {
        title: data.title as string,
        description: data.description as string,
        tags: uniqueTags,
        ...(isEditing ? await resolvePreviewMediaId() : {}),
      };

      if (isEditing && handlePlaylistUpdate) {
        await handlePlaylistUpdate(constructedPlaylist);
        handleClose(false);

        return;
      }

      if (isPendingAddNewNugget) {
        await handlePlaylistCreate(constructedPlaylist);

        return;
      }

      await handlePlaylistCreate(constructedPlaylist);
      handleClose(false);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      console.error('Error saving playlist', e);
    }
  };

  return (
    <InfoModal
      open={true}
      width="sm"
      onClose={() => handleClose(false)}
      hideCircleImage
      featuredIcon={{
        iconName: 'Flag05',
        size: 'lg',
        color: 'Colors/Foreground/fg-secondary',
      }}
      headerInLineWithIcon
      showBottomBorder
      dialogSx={dialogSx}
      headerContent={
        <Box>
          <Typography variant="text-lg-semibold">
            {isEditing ? PLAYLIST_DIALOG_STRINGS.editTitle : PLAYLIST_DIALOG_STRINGS.addPlaylist}
          </Typography>
          <Typography variant="text-sm-regular">
            {isEditing
              ? PLAYLIST_DIALOG_STRINGS.editDescription
              : PLAYLIST_DIALOG_STRINGS.description}
          </Typography>
        </Box>
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box>
          <Box sx={formContainerSx}>
            <BackrsForm fields={fields} onSubmit={handleSubmit} methods={methods} />

            {isEditing && (
              <Box sx={imageSectionSx}>
                <Typography variant="text-sm-medium">
                  {PLAYLIST_DIALOG_STRINGS.imageLabel}
                </Typography>

                <input
                  id="playlist-dialog-image-input"
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                />

                {displayedImageUrl ? (
                  <Box component="img" src={displayedImageUrl} alt="" sx={imagePreviewSx} />
                ) : (
                  <Box sx={imagePlaceholderSx} aria-hidden>
                    {playlistToEdit && (
                      <MeshGradient config={buildSeededMeshConfig(playlistToEdit.id)} />
                    )}
                  </Box>
                )}

                <Box sx={imageActionsSx}>
                  {displayedImageUrl && (
                    <Tooltip title={PLAYLIST_DIALOG_STRINGS.removeImage} disableInteractive>
                      <Button
                        id="playlist-dialog-image-remove-button"
                        sx={removeImageButtonSx}
                        onClick={handleImageRemove}
                        aria-label={PLAYLIST_DIALOG_STRINGS.removeImage}
                      >
                        <AppIcon iconName="Trash01" diameter="20px" color="currentColor" />
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    id="playlist-dialog-image-upload-button"
                    variant="outlined"
                    hierarchy="Secondary gray"
                    size="sm"
                    icon="Leading"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={
                      <AppIcon iconName="UploadCloud02" diameter="20px" color="currentColor" />
                    }
                  >
                    {PLAYLIST_DIALOG_STRINGS.uploadImage}
                  </Button>
                </Box>

                {imageError && (
                  <Typography
                    variant="text-sm-regular"
                    color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
                  >
                    {imageError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {submitError && (
            <Typography
              id="playlist-dialog-submit-error"
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
              sx={submitErrorSx}
            >
              {submitError}
            </Typography>
          )}

          <Box sx={buttonContainerSx}>
            <Button
              id="playlist-dialog-cancel-button"
              variant="contained"
              hierarchy="Secondary gray"
              fullWidth
              size="lg"
              onClick={() => handleClose(false)}
            >
              {PLAYLIST_DIALOG_STRINGS.cancel}
            </Button>
            <Button
              id="playlist-dialog-submit-button"
              variant="contained"
              hierarchy="Primary"
              fullWidth
              size="lg"
              onClick={() => handleSubmit(methods.getValues())}
              disabled={!isFormStateValid || isUploadingImage}
            >
              {isUploadingImage && PLAYLIST_DIALOG_STRINGS.uploadingImage}
              {!isUploadingImage &&
                (isEditing
                  ? PLAYLIST_DIALOG_STRINGS.updatePlaylist
                  : PLAYLIST_DIALOG_STRINGS.addPlaylist)}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-none'],
  marginX: -backrs.spacing['spacing-3xl'],
  width: '100%',
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-2xl'],
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  p: backrs.spacing['spacing-3xl'],
});

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
});

const submitErrorSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-md'],
});

const imageSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  mt: backrs.spacing['spacing-xl'],
});

const imagePreviewSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: 160,
  objectFit: 'cover',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

// Matches the preview's footprint, and positions the gradient that paints itself absolutely.
const imagePlaceholderSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: 160,
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const removeImageButtonSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 0,
  minHeight: backrs.spacing['spacing-5xl'],
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  borderRadius: backrs.radius['radius-md'],
});

const imageActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xs'],
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '640px', // specific pixel width to match Figma
    width: '640px',
    margin: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-4xl'] },
  },
});
